import {
  type ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { useHorizontalWheelScroll } from '../hooks/useHorizontalWheelScroll'
import { mergeRefs } from '../lib/mergeRefs'

type Props = ComponentPropsWithoutRef<'div'> & {
  /** 内层 w-max 轨道（flex + gap 等），保证出现横向溢出 */
  trackClassName?: string
}

function normalizeWheelDeltaY(el: HTMLElement, e: WheelEvent): number {
  let dy = e.deltaY
  if (e.deltaMode === 1) dy *= 16
  else if (e.deltaMode === 2) dy *= el.clientHeight || 480
  return dy
}

/**
 * 横向芯片条：内层 w-max 轨道 + 纵向滚轮转横向。
 * 滚轮须用原生 passive:false（React onWheel 为 passive，无法 preventDefault）。
 */
export const HorizontalChipStrip = forwardRef<HTMLDivElement, Props>(function HorizontalChipStrip(
  { className = '', trackClassName = 'gap-2', children, ...rest },
  forwardedRef,
) {
  const innerRef = useRef<HTMLDivElement>(null)
  const overflowRef = useHorizontalWheelScroll<HTMLDivElement>({ mapVerticalWheelToHorizontal: false })
  const mergedRef = useMemo(
    () => mergeRefs(overflowRef, forwardedRef, innerRef),
    [overflowRef, forwardedRef],
  )

  useEffect(() => {
    const el = innerRef.current
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return

      const max = Math.max(0, el.scrollWidth - el.clientWidth)
      if (max <= 2) return

      const dy = normalizeWheelDeltaY(el, e)
      const next = Math.max(0, Math.min(max, el.scrollLeft + dy))
      if (Math.abs(next - el.scrollLeft) <= 0.75) return

      e.preventDefault()
      e.stopPropagation()
      el.scrollLeft = next
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  return (
    <div
      ref={mergedRef}
      className={[
        'scroll-x-chipstrip scroll-x-wheel min-h-0 min-w-0 w-full max-w-full overflow-x-auto overflow-y-hidden',
        className,
      ].join(' ')}
      {...rest}
    >
      <div className={['flex w-max min-w-0 flex-nowrap items-center', trackClassName].join(' ')}>
        {children}
      </div>
    </div>
  )
})
